import { FC, PropsWithChildren, useEffect } from 'react';
import { ConfigProvider } from './config-provider';
import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { ClassSubjectProvider } from './lesson-plan-providers/class-subject-provider';
import { ClassSubjectChipProvider } from './lesson-plan-providers/class-subject-chip-provider';
import { ExamProvider } from './exam-generator-provider/exam-generator-provider';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { TimetableProvider } from './lesson-plan-providers/timetable-selection-provider';
import {
  BottomSheetProvider,
  useBottomSheetContext,
} from './bottom-sheet-provider';
import { RegionProvider } from './region-provider';
import { ReportCardProvider } from './report-card-provider';
import { RolesPermissionProvider } from './active-roles-permission-provider';
import { AbilityContextProvider } from './access-provider';
import { AudioSyncProvider } from '@taleemabad/shared';

const Providers: FC<PropsWithChildren> = ({ children }) => {
  const { isBottomSheetOpen, setIsBottomSheetOpen } = useBottomSheetContext();

  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      const navBackUrls = ['/login', '/timetable'];
      CapacitorApp.addListener('backButton', ({ canGoBack }) => {
        // If bottom sheet is open, close it before handling back button logic to prevent minimizing the app
        if (isBottomSheetOpen) {
          setIsBottomSheetOpen(false);
          return;
        }
        if (!canGoBack || navBackUrls.includes(window.location.pathname)) {
          CapacitorApp.minimizeApp();
        } else {
          window.history.back();
        }
      });

      return () => {
        CapacitorApp.removeAllListeners();
      };
    }
  }, [isBottomSheetOpen]);

  return (
    <>
      <ToastContainer
        autoClose={1500}
        position="top-center"
        theme="dark"
        icon={false}
        closeButton={false}
        limit={1}
        hideProgressBar={false}
        draggablePercent={60}
        bodyClassName="toast-message-text"
      />
      {children}
    </>
  );
};

// List of providers
const providers = [
  BottomSheetProvider,
  RolesPermissionProvider,
  AbilityContextProvider,
  RegionProvider,
  ConfigProvider,
  TimetableProvider,
  ClassSubjectProvider,
  ExamProvider,
  ClassSubjectChipProvider,
  ReportCardProvider,
  AudioSyncProvider,
];

export function AppProvider({ children }: PropsWithChildren) {
  const CombinedProviders = composeProviders(providers);
  return (
    <CombinedProviders>
      <Providers>{children}</Providers>
    </CombinedProviders>
  );
}

const composeProviders = (providers: FC<PropsWithChildren>[]) => {
  return providers.reduce(
    (AccumulatedProviders, CurrentProvider) =>
      ({ children }: PropsWithChildren) => (
        <AccumulatedProviders>
          <CurrentProvider>{children}</CurrentProvider>
        </AccumulatedProviders>
      ),
    ({ children }: PropsWithChildren) => <>{children}</>,
  );
};
